import { inject, Injectable, Optional, ViewContainerRef } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { combineLatest, merge, of, switchMap } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { filterBoolean, Helpers } from '@recruitee/common';
import { SubscriptionService } from '@recruitee/company-logic';
import { CustomFieldsHelperService } from '@recruitee/custom-fields-util';
import { I18nService } from '@recruitee/i18n';
import type { JobEditorNavigationExtraState } from '@recruitee/job-editors-shared-types';
import { LocationsHelperService } from '@recruitee/locations';
import { LocationSelectors } from '@recruitee/locations-data';
import type { JobValidationErrors } from '@recruitee/offers';
import { JobSchedulerFacade, JobValidationFormErrorType } from '@recruitee/offers';
import { JobItemsSelectors, JobsActions, StatusChangeType } from '@recruitee/offers-data';
import { JobPublishedDialogFacade } from '@recruitee/offers-logic';
import type { OfferItem } from '@recruitee/offers-types';
import {
  OfferKind,
  OfferStatus,
  EmploymentType,
  JobEditorDialogTab,
} from '@recruitee/offers-types';
import { isRequisitionRequired$ } from '@recruitee/offers-util';
import { OfferSlotsLimitDialogComponent } from '@recruitee/pricing';
import type { DialogConfirmOptions } from '@recruitee/user-interface';
import { DialogService, DialogWidth, NotificationsController } from '@recruitee/user-interface';

import { JobsStatusAnalyticsHelperService } from '../jobs-status-analytics.helper';
import type { StatusChangeFacade, StatusChangeParam } from './status-change-facade.types';
import { StatusChangeTexts } from './status-change-texts.service';

@Injectable()
export class StatusChangeDefaultFacade implements StatusChangeFacade {
  private readonly customFieldsHelper = inject(CustomFieldsHelperService);

  constructor(
    private store: Store,
    private actions$: Actions,
    private subscription: SubscriptionService,
    protected dialog: DialogService,
    protected vcRef: ViewContainerRef,
    protected jobSchedulerFacade: JobSchedulerFacade,
    protected i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private texts: StatusChangeTexts,
    private router: Router,
    @Optional()
    private jobsStatusAnalytics: JobsStatusAnalyticsHelperService,
    private locationsHelper: LocationsHelperService,
    private jobPublishedDialogFacade: JobPublishedDialogFacade,
  ) {}

  public setClosed(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Closed)) return;
    this.confirmClose$(param).subscribe(() => this.execute(param, StatusChangeType.Close));
  }

  public setInternal(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Internal)) return;
    this.verifyIssues(param.id, OfferStatus.Internal)
      .pipe(filter(validationErrors => !validationErrors))
      .subscribe(() => this.execute(param, StatusChangeType.Unpublish));
  }

  public setPublished(param: StatusChangeParam) {
    if (!this.verifySubscription(param, OfferStatus.Published)) return;
    this.verifyIssues(param.id, OfferStatus.Published)
      .pipe(filter(validationErrors => !validationErrors))
      .subscribe(() => this.execute(param, StatusChangeType.Publish));
  }

  public confirmClose$(param: StatusChangeParam): Observable<boolean> {
    const dialogOptions =
      param.status === OfferStatus.Archived
        ? { ...this.texts.model.retrieve }
        : { ...this.texts.model.close };

    if (this.jobSchedulerFacade.hasScheduledDates(param.jobScheduler)) {
      dialogOptions.message = this.jobSchedulerFacade.getCloseConfirmDialogMessage(
        param.jobScheduler,
      );
    }

    return this.openConfirmDialog(dialogOptions).pipe(filterBoolean);
  }

  private execute(param: StatusChangeParam, name: StatusChangeType) {
    this.setupEffects(param, name);
    this.store.dispatch(
      JobsActions.changeStatus({
        payload: { id: param.id, status: name, currentStatus: param.status },
      }),
    );
  }

  private setupEffects(param: StatusChangeParam, action: StatusChangeType) {
    merge(
      this.actions$.pipe(
        ofType(JobsActions.changeStatusSuccess),
        tap(() => this.success(action, param.id)),
      ),
      this.actions$.pipe(
        ofType(JobsActions.changeStatusFailed),
        tap(({ payload }) => this.failed(payload.error)),
      ),
    )
      .pipe(take(1))
      .subscribe();
  }

  private success(name: StatusChangeType, jobId: number) {
    if ([StatusChangeType.Publish, StatusChangeType.Unpublish].includes(name)) {
      this.jobPublishedDialogFacade.open(jobId);
    } else {
      this.notificationsCtrl.success(
        this.i18n.translate(`app.offers.actions.${name}.success_message.job`),
      );
    }

    if (name === StatusChangeType.Publish || name === StatusChangeType.Unpublish) {
      const newStatus =
        name === StatusChangeType.Publish ? OfferStatus.Published : OfferStatus.Internal;
      this.jobsStatusAnalytics.trackJobStatusChange(jobId, newStatus);
    }
    if (name === StatusChangeType.Close) {
      this.jobsStatusAnalytics.trackJobClose(jobId);
    }
  }

  private failed(error: { errorFields: any }) {
    let slotsLimitExceeded = false;
    const errorKeys = Object.keys(error.errorFields);
    const errors = errorKeys.map(errorKey => {
      const translationKey = Helpers.underscore(errorKey);
      if (errorKey === 'slotsLimitExceeded') {
        slotsLimitExceeded = true;

        return {
          key: errorKey,
          text: '',
        };
      }
      return {
        key: errorKey,
        text: this.i18n.translate(`app.offer_form.errors.${translationKey}`),
      };
    });

    if (slotsLimitExceeded && this.subscription.isTrial()) {
      this.dialog.custom(
        OfferSlotsLimitDialogComponent,
        { width: DialogWidth.Small },
        { offerKind: OfferKind.Job, isTrial: true },
      );
    } else {
      this.notificationsCtrl.error({
        title: this.i18n.translate('app.common.error_something_went_wrong'),
        messageHTML: `<ul>${errors.map(errorField => `<li>${errorField.text}</li>`).join('')}</ul>`,
      });
    }
  }

  private openConfirmDialog(options: DialogConfirmOptions): Observable<boolean> {
    return this.dialog.information({ ...options, isCancelable: true }).pipe(filterBoolean);
  }

  private verifySubscription(param: StatusChangeParam, nextStatus: OfferStatus): boolean {
    const isActionAllowed = this.subscription.isStatusChangeAllowed(param.status, nextStatus);

    if (!isActionAllowed) {
      this.openSlotsLimitDialog();
      return false;
    }

    return true;
  }

  private verifyIssues(jobId: number, nextStatus: OfferStatus): Observable<JobValidationErrors> {
    let job: OfferItem;
    let isRequisitionRequired: boolean;
    let skipValidation = false;

    return combineLatest({
      job: this.store.select(JobItemsSelectors.byId(jobId)).pipe(filterBoolean),
      isRequisitionRequired: isRequisitionRequired$(this.store),
    }).pipe(
      tap(data => {
        job = data.job;
        isRequisitionRequired = data.isRequisitionRequired;
        skipValidation =
          job.status === OfferStatus.Published && nextStatus !== OfferStatus.Published;
      }),
      switchMap(data =>
        combineLatest([
          this.locationsErrors(data),
          this.isEmploymentTypeInvalid(data),
          this.customFieldsErrors(data),
        ]),
      ),
      map(
        ([
          { isActiveValidLocationMissing, exceededLocationsLimit, isActiveLocationMissing },
          invalidEmploymentType,
          { exceededCustomFieldsLimit },
        ]) => {
          if (!job.issues) return null;
          let validationErrors: JobValidationErrors = skipValidation
            ? null
            : {
                formErrorType: job.issues.isRequiredDataMissing
                  ? JobValidationFormErrorType.MissingRequiredFields
                  : null,
                isRequisitionMissing: isRequisitionRequired
                  ? job.issues.isRequisitionMissing
                  : false,
                isApprovedRequisitionMissing: isRequisitionRequired
                  ? job.issues.isRequisitionMissing
                  : false,
                invalidEmploymentType,
                exceededLocationsLimit,
                isActiveValidLocationMissing,
                isActiveLocationMissing,
                exceededCustomFieldsLimit,
              };
          if (validationErrors && Object.values(validationErrors).every(val => !val)) {
            validationErrors = null;
          }
          return { validationErrors, jobId: job.id };
        },
      ),
      tap(result => {
        if (!result) return;
        this.validationFailedEffect(result.validationErrors, result.jobId);
      }),
      map(result => (result ? result.validationErrors : null)),
      take(1),
    );
  }

  private validationFailedEffect(validationErrors: JobValidationErrors, jobId: number) {
    if (!validationErrors) return;
    const viewUrl = { outlets: { 'job-editor': [jobId, JobEditorDialogTab.JobDetails] } };
    const toastRef = this.notificationsCtrl.error({
      title: this.i18n.translate('app.offers.notifications.generic_form_error_title'),
      message: this.i18n.translate('app.offers.notifications.generic_form_error_message'),
      topButtonLabel: this.i18n.translate('app.offers.view'),
    });

    toastRef
      .topButtonClick()
      .pipe(take(1), takeUntil(toastRef.afterClosed))
      .subscribe(() => {
        const state: JobEditorNavigationExtraState = {
          validationFailed: true,
        };
        this.router.navigate([viewUrl], {
          state,
        });
      });
  }

  private openSlotsLimitDialog() {
    const dialogConfig = {
      component: OfferSlotsLimitDialogComponent,
      config: { width: DialogWidth.Small },
      data: { offerKind: OfferKind.Job, isTrial: this.subscription.isTrial() },
    };

    this.dialog.custom(<any>dialogConfig.component, dialogConfig.config, dialogConfig.data);
  }

  private locationsErrors(data: { job: OfferItem }) {
    return combineLatest([
      this.store.select(LocationSelectors.byIds(data.job.locationIds || [])),
      this.locationsHelper.getLocationsLimit(),
    ]).pipe(
      take(1),
      switchMap(([locations, locationsLimit]) => {
        const exceededLocationsLimit = locations.length > locationsLimit;
        const isActiveLocationMissing = !locations.some(location => !location.archivedAt);
        const isActiveValidLocationMissing = !locations.some(
          location => !location.archivedAt && location.isValid,
        );

        return of({
          isActiveLocationMissing,
          isActiveValidLocationMissing,
          exceededLocationsLimit,
        });
      }),
    );
  }

  private customFieldsErrors(data: { job: OfferItem }) {
    const exceededCustomFieldsLimit = this.customFieldsHelper.getIsCustomFieldsLimitExceeded(
      data.job.dynamicFieldsCount ?? 0,
    );
    return of({ exceededCustomFieldsLimit });
  }

  private isEmploymentTypeInvalid(data: { job: OfferItem }): Observable<boolean> {
    const employmentType = data.job.employmentType;
    const isInvalid = [EmploymentType.FullTime, EmploymentType.PartTime].includes(
      <EmploymentType>employmentType,
    );
    return of(isInvalid);
  }
}
